import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import Modal from 'Common/components/Modals/Modal';
import Button from 'Common/components/Button';
import ModalTitle from 'Common/components/Modals/ModalTitle';
import { useFormik } from 'formik';
import axios from 'axios';
import notify from 'Common/utils/notify';
import { toast } from 'react-toastify';
import { omit } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
import ContactPeopleFields from 'ProjectManager/Project/DataManagement/GeneralInformation/ContactPeople/components/ContactPeopleFields';
import getClient from 'ProjectManager/Client/api/getClient';
import isClientContactPersonEmailUnique from 'ProjectManager/Client/api/contactPeople/isClientContactPersonEmailUnique';
import addContactPeopleToClient from 'ProjectManager/Client/api/contactPeople/addContactPeopleToClient';
import { useWindowWidth } from '@react-hook/window-size';

const initialValues = {
    contactPeople: [
        {
            id: uuidv4(),
            name: '',
            email: '',
        },
    ],
};

const AddMoreContactPeopleModal = ({
    client,
    onAddMore,
    onCancel,
    ...props
}) => {
    const [isAdding, setIsAdding] = useState(false);

    const source = useMemo(() => axios.CancelToken.source(), []);

    useEffect(
        () => () => {
            source.cancel();
        },
        [source],
    );

    const uniqueEmailValidationCallback = useCallback(
        async value => {
            if (!value) {
                return true;
            }

            try {
                const response = await isClientContactPersonEmailUnique(
                    value,
                    client.id,
                    null,
                    source.token,
                );

                return response.data.isUnique;
            } catch (error) {
                return axios.isCancel(error);
            }
        },
        [client.id, source.token],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                contactPeople: Yup.array()
                    .of(
                        Yup.object()
                            .shape({
                                name: Yup.string().required('Pflichtangabe'),
                                email: Yup.string()
                                    .email('Ungültige E-Mail')
                                    .debouncedCallback(
                                        'E-Mail bereits in Verwendung',
                                        uniqueEmailValidationCallback,
                                    )
                                    .required('Pflichtangabe'),
                            })
                            .uniqueProperty(
                                'email',
                                'E-Mail bereits in Verwendung',
                            ),
                    )
                    .required('Pflichtangabe'),
            }),
        [uniqueEmailValidationCallback],
    );

    const {
        errors,
        touched,
        values,
        setFieldValue,
        setFieldTouched,
        setFieldError,
        handleChange,
        handleBlur,
        handleSubmit,
        isValid,
    } = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async values => {
            setIsAdding(true);

            try {
                await addContactPeopleToClient(
                    client.id,
                    values.contactPeople.map(contactPerson => ({
                        ...omit(contactPerson, ['id']),
                    })),
                    source.token,
                );

                const response = await getClient(
                    client.id,
                    ['contactPeople'],
                    source.token,
                );

                const clientWithContactPeople = response.data;

                const newlyAddedEmailAddresses = values.contactPeople.map(
                    contactPerson => contactPerson.email,
                );

                const newlyAddedContactPeople = clientWithContactPeople.contactPeople.filter(
                    contactPerson =>
                        newlyAddedEmailAddresses.includes(contactPerson.email),
                );

                // Let the parent component know about the change that was made
                // and which are the newly added contact people.
                await onAddMore(newlyAddedContactPeople);
            } catch (error) {
                if (!axios.isCancel(error)) {
                    notify(
                        'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                        {
                            type: toast.TYPE.ERROR,
                        },
                    );
                }
            }

            setIsAdding(false);
        },
    });

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 940;

    const modalRef = useRef(null);

    const setModalRef = node => {
        modalRef.current = node;
    };

    const handleContactPersonAdd = () => {
        if (isMobile && modalRef.current) {
            // The timeout is to ensure that the state will be set
            // and the distance to the button will be calculated properly.
            setTimeout(() => {
                modalRef.current.scroll({
                    top: modalRef.current.scrollHeight,
                    left: 0,
                    behavior: 'smooth',
                });
            }, 5);
        }
    };

    return (
        <Modal
            contentRef={setModalRef}
            maxHeight={500}
            onRequestClose={isAdding ? undefined : onCancel}
            buttons={
                <>
                    <Button
                        text
                        dark
                        type="button"
                        onClick={onCancel}
                        disabled={isAdding}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        type="button"
                        isLoading={isAdding}
                        onClick={handleSubmit}
                        disabled={isAdding || !isValid}
                    >
                        Hinzufügen
                    </Button>
                </>
            }
            {...props}
        >
            <form onSubmit={handleSubmit}>
                <ModalTitle>Weitere hinzufügen</ModalTitle>
                <p>{`Dieser Kontakt wird der "${client.name}" hinzugefügt`}</p>

                <ContactPeopleFields
                    contactPeople={values.contactPeople}
                    errors={errors.contactPeople ?? []}
                    touched={touched.contactPeople ?? []}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    setFieldTouched={setFieldTouched}
                    onContactPersonAdd={handleContactPersonAdd}
                />
            </form>
        </Modal>
    );
};

AddMoreContactPeopleModal.propTypes = {
    client: PropTypes.object.isRequired,
    onAddMore: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

export default AddMoreContactPeopleModal;
